// hooks/index.ts
import { useState, useEffect, useRef } from 'react';
import { useWebPay } from './useWebPay';
import { WebPayHookOptions, PaymentRequest } from '../types';

export interface WebPayModalState {
  isLoading: boolean;
  error: string | null;
  hasInitiated: boolean;
  initiatePayment: () => void;
  reset: () => void;
  close: () => void;
}

export interface UseWebPayModalProps {
  isOpen: boolean;
  onClose: () => void;
  paymentRequest: PaymentRequest;
  options?: WebPayHookOptions;
}

export const useWebPayModal = ({
  isOpen,
  onClose,
  paymentRequest,
  options,
}: UseWebPayModalProps): WebPayModalState => {
  const { initiatePayment, isLoading, error } = useWebPay();
  const [hasInitiated, setHasInitiated] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const enhancedOptions: WebPayHookOptions = {
    onSuccess: (response) => {
      console.log('Payment successful:', response);
      options?.onSuccess?.(response);
      onClose();
    },
    onError: (error) => {
      console.log('Payment error:', error);
      options?.onError?.(error);
    },
    onCancel: () => {
      console.log('Payment cancelled');
      options?.onCancel?.();
      onClose();
    },
  };

  const handleInitiatePayment = () => {
    if (!hasInitiated && !isLoading) {
      console.log('Initiating payment...', paymentRequest);
      setHasInitiated(true);
      initiatePayment(paymentRequest, enhancedOptions);
    }
  };

  const reset = () => {
    setHasInitiated(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  // Auto-initiate payment when modal opens with a slight delay
  useEffect(() => {
    if (isOpen && !hasInitiated && !isLoading) {
      // Small delay to ensure DOM is ready
      timeoutRef.current = setTimeout(() => {
        handleInitiatePayment();
      }, 100);
    }
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isOpen, hasInitiated, isLoading]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHasInitiated(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  }, [isOpen]);

  return {
    isLoading,
    error,
    hasInitiated,
    initiatePayment: handleInitiatePayment,
    reset,
    close: onClose,
  };
};